import type React from "react";

import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  InputAdornment,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search,
  CalendarToday,
  Business,
  School,
  Numbers,
} from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useSelector } from "react-redux";
import OverlayLoader from "../../../../components/OverlayLoader";

import HttpClient from "../../../../helpers/HttpClient";
import { showToast } from "../../../../utils/toast";
import { getBinaryContent } from "../../../../utils/electronFunctions";
import { confirmAlert } from "react-confirm-alert";

interface CertificatesData {
  id: number;
  constancia: number;
  fecha: string;
  distribuidor: string;
  nombrecurso: string;
  nombrearchivo: string;
  cod_curso: string;
  distribuidorid: number;
}

interface PaginationResponse {
  data: CertificatesData[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    offset: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Función que simula la llamada al servidor
const fetchLotesFromServer = async (
  page: number,
  limit: number,
  search: string = ""
): Promise<PaginationResponse> => {
  const result = await HttpClient().get(
    `/admin/distributor/certificates/list?page=${page}&limit=${limit}&search=${search}`
  );
  if (result.error) {
    showToast(
      "No se pudo cargar información de distribuidores/empresas. Refresca la página e intenta nuevamente.",
      "error"
    );
  }

  return result.data;
};

export default function CertificatesListPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [debouncedSearch] = useDebounce(searchTerm, 500);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLote, setSelectedLote] = useState<CertificatesData | null>(
    null
  );
  const [openLoader, setOpenLoader] = useState(false);
  const [certificatesData, setCertificatesData] = useState<CertificatesData[]>(
    []
  );
  const authState = useSelector((state) => state.auth);
  const userId = authState.user ? authState.user.email : "test";
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    limit: 10,
    offset: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Función para cargar datos del servidor
  const loadData = async (
    currentPage: number,
    limit: number,
    search: string = ""
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchLotesFromServer(currentPage, limit, search);
      setCertificatesData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError("Error al cargar los datos del servidor");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadData(page, rowsPerPage, debouncedSearch);
  }, [page, rowsPerPage, debouncedSearch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const deleteCertificate = async (certificate) => {
    confirmAlert({
      title: "Confirmar",
      message:
        "Se eliminará el fichero y los datos asociados. ¿Deseas continuar?",
      buttons: [
        {
          label: "Sí",
          onClick: async () => await deleteCertificateExec(certificate),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const deleteCertificateExec = async (certificate) => {
    setOpenLoader(true);
    const result = await HttpClient().delete(
      `/admin/distributor/certificates/delete/${certificate.id}/${certificate.distribuidorid}/${userId}`
    );

    setOpenLoader(false);
    if (result.error) {
      showToast(
        "No se pudo realizar la eliminación. Intenta nuevamente.",
        "error"
      );
      return;
    }

    if (result.status === 202) {
      showToast("Proceso finalizado exitosamente.", "success");
      loadData(page, rowsPerPage, debouncedSearch);
    } else {
      showToast(
        "No se pudo realizar la operación. Intenta nuevamente.",
        "warning"
      );
    }
  };

  const openFileInBrowser = async (certificate) => {
    setOpenLoader(true);
    const pdfDocumentPath = `/constancias/${certificate.distribuidorid}/${certificate.nombrearchivo}.pdf`;
    const fileBytes = await getBinaryContent(pdfDocumentPath);
    const blob = new Blob([fileBytes], {
      type: "application/pdf",
    });

    // Crear URL del blob
    const blobUrl = URL.createObjectURL(blob);

    // Abrir en nueva pestaña
    window.open(blobUrl, "_blank");

    // Opcional: Liberar la URL después de un tiempo
    setTimeout(() => {
      setOpenLoader(false);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = Number.parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
  };

  const handleRowClick = (lote: CertificatesData) => {
    ///todo
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      <OverlayLoader open={openLoader} />
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{ fontSize: "1.3rem", mb: 2 }}
      >
        Lista de contancias
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ mb: 3, fontSize: "0.8rem" }}
      >
        Gestión y visualización de contancias personalizadas por
        Distribuidor/Empresa y Curso
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ borderRadius: 2, border: "1px solid #E5E7EB" }}>
        <CardContent sx={{ p: 2 }}>
          {/* Controles superiores */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontSize: "0.75rem" }}
              >
                Mostrar de a
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontSize: "0.8rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontSize: "0.75rem" }}
              >
                registros
              </Typography>
            </Box>

            <TextField
              placeholder="Buscar por distribuidor/empresa, cursos, lote o fecha..."
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
              size="small"
              sx={{
                minWidth: 250,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F9FAFB",
                  fontSize: "0.8rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#9CA3AF", fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabla */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 1.5,
              border: "1px solid #E5E7EB",
              position: "relative",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#64748B" }}>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      <Numbers sx={{ fontSize: 16 }} />
                      Lote
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      <CalendarToday sx={{ fontSize: 16 }} />
                      Fecha
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      <Business sx={{ fontSize: 16 }} />
                      Distribuidor/Empresa
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      <School sx={{ fontSize: 16 }} />
                      Curso
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "center",
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        justifyContent: "center",
                      }}
                    >
                      <Numbers sx={{ fontSize: 16 }} />
                      Nombre archivo
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      py: 1,
                      textAlign: "center",
                      fontSize: "0.8rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        justifyContent: "center",
                      }}
                    >
                      <Numbers sx={{ fontSize: 16 }} />
                      Acciones
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificatesData.map((certificate, index) => (
                  <TableRow
                    key={certificate.id}
                    onClick={() => !loading && handleRowClick(certificate)}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#FAFAFA" : "white",
                      "&:hover": {
                        backgroundColor: loading ? undefined : "#F0F9FF",
                        cursor: loading ? "default" : "pointer",
                      },
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 1,
                        fontWeight: "bold",
                        color: "#374151",
                        fontSize: "0.8rem",
                      }}
                    >
                      {certificate.constancia}
                    </TableCell>
                    <TableCell
                      sx={{ py: 1, color: "#6B7280", fontSize: "0.75rem" }}
                    >
                      {formatDate(certificate.fecha)}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight="600"
                        color="#374151"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {certificate.distribuidor}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, maxWidth: 250 }}>
                      {certificate.cod_curso} - {certificate.nombrecurso}
                    </TableCell>
                    <TableCell sx={{ py: 1, textAlign: "center" }}>
                      <Chip
                        label={certificate.nombrearchivo + ".pdf"}
                        size="small"
                        sx={{
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, textAlign: "center" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Ver fichero" arrow>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => openFileInBrowser(certificate)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(0, 83, 117, 0.1)",
                              },
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar" arrow>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => deleteCertificate(certificate)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(255, 0, 0, 0.1)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Información y paginación */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontSize: "0.75rem" }}
            >
              Mostrando {pagination.offset + 1} a{" "}
              {Math.min(pagination.offset + pagination.limit, pagination.total)}{" "}
              de {pagination.total} registros
              {searchTerm && ` (filtrados)`}
              <br />
              <Typography
                component="span"
                variant="caption"
                color="primary"
                sx={{ fontStyle: "italic", fontSize: "0.7rem" }}
              >
                Haz clic en cualquier fila para gestionar el lote
              </Typography>
            </Typography>

            <TablePagination
              component="div"
              count={pagination.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
              rowsPerPageOptions={[]}
              disabled={loading}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  minHeight: "auto",
                },
                "& .MuiTablePagination-selectLabel": {
                  display: "none",
                },
                "& .MuiTablePagination-select": {
                  display: "none",
                },
                "& .MuiTablePagination-displayedRows": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
